import type { TreeSelectionType } from '#root/domain/core/filterDefinition/types/treeSelection.type.ts';
import type { InputSearchModelType } from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';

type TreePath = TreeSelectionType['path'];

export const isSamePath = (a: TreePath, b: TreePath) =>
  a.length === b.length &&
  a.every((segment, index) => segment.type === b[index]?.type && segment.key === b[index]?.key);

export const isPathPrefix = (prefix: TreePath, path: TreePath) =>
  prefix.length < path.length && isSamePath(prefix, path.slice(0, prefix.length));

export const hasSearch = (input: InputSearchModelType | undefined) => {
  const value = input?.searchValue;
  const values = Array.isArray(value) ? value : [value];

  return values.some((item) => !!item?.trim());
};

/**
 * Merges two option trees, nodes being identified by their level type and key.
 */
export function mergeTreeOptions(
  base: FilterDefinitionValue[],
  incoming: FilterDefinitionValue[]
): FilterDefinitionValue[] {
  const merged = base.map((node) => ({ ...node }));

  incoming.forEach((node) => {
    const existing = merged.find((item) => item.value === node.value && item.type === node.type);

    if (!existing) {
      merged.push(node);
      return;
    }

    if (node.children?.length) {
      existing.children = mergeTreeOptions(existing.children ?? [], node.children);
    }
  });

  return merged;
}

/**
 * Keeps the nodes covering the selections: a selected node with its whole subtree, and its ancestors.
 */
export function pruneTreeOptionsToSelections(
  options: FilterDefinitionValue[],
  selections: TreeSelectionType[],
  parentPath: TreePath = []
): FilterDefinitionValue[] {
  return options.flatMap((node) => {
    const path = [...parentPath, { type: node.type, key: node.value }] as TreePath;

    if (selections.some((selection) => isSamePath(selection.path, path))) {
      return [node];
    }

    if (!selections.some((selection) => isPathPrefix(path, selection.path))) {
      return [];
    }

    return [{ ...node, children: pruneTreeOptionsToSelections(node.children ?? [], selections, path) }];
  });
}
